// Stages picks (pendingAdditions.js) as one pending Watchlist change, for the
// grid's "+ Add security" Save and the Build Watchlist additions dialog: the
// listings in one call, then each identifier to look up on Apply. Every call
// amends the one pending change, so it stays one review.
import { showWatchlistDraftGuidance } from '../../../utils/watchlistDraftGuidance';
import {
    stageSecurityResolutionAdditions,
    stageSecurityResolutionUnresolvedAddition,
} from '../../../utils/ApiCall';
import { parseCloudRunError } from '../../msiDriftReviewHelpers';
import { isStaleStageCode, staleStageItemIndices } from '../securityAdditionContract';
import {
    isListingPick,
    markStalePicks,
    markStaleStageItems,
    removePick,
    toStageItems,
    toUnresolvedStageRequests,
} from './pendingAdditions';

const REVIEWABLE_STAGE_STATUSES = ['pending_review', 'pending_apply'];

// One addition Stage call's outcome: the pending review it staged (null when
// the server staged nothing new, with its message), or why it failed. A
// pending change that cannot take the addition shows the draft guidance.
function additionStageOutcome(response, sessionScopeToken) {
    if (response?.success === false) {
        const parsed = parseCloudRunError(response?.message || response);
        if (showWatchlistDraftGuidance({ ...parsed, ...response }, sessionScopeToken)) {
            return { ok: false, message: '' };
        }
        const code = response?.code || parsed.code;
        return {
            ok: false,
            stale: isStaleStageCode(code),
            detail: response?.detail || parsed.detail,
            message: parsed.message || 'Could not prepare the additions for review.',
        };
    }
    const revisionId = String(
        response?.watchlist_revision_id || response?.pending_revision?.id || '',
    ).trim();
    if (!revisionId) return { ok: true, review: null, message: response?.message || '' };
    const pending = response?.pending_review === true || Boolean(response?.pending_revision);
    if (
        response?.success !== true
        || !pending
        || (response.status && !REVIEWABLE_STAGE_STATUSES.includes(response.status))
    ) {
        return { ok: false, message: 'The addition review is unavailable. Retry the same selection.' };
    }
    return { ok: true, review: { revisionId, revisionSeq: response.revision_seq } };
}

/**
 * Stage `picks`. Staged picks leave the picks; the first failure stops the
 * rest (out-of-date choices are marked instead, which blocks staging until
 * they are removed or picked again).
 *
 * `attempts` maps each request to its attempt id, so retrying the same
 * request reuses it (`newAttemptId` makes one). `isCurrent()` false after a
 * call means the caller moved on: the result is null. `onMarkedStale`
 * receives the picks before and after each stale marking.
 *
 * Returns { picks, review, failure, notice }: the picks left, the last review
 * staged ({ revisionId, revisionSeq }) or null, why staging stopped, and the
 * server's message when it staged nothing new.
 */
export async function stagePicks(picks, {
    expectedWatchlistRevisionId,
    sessionScopeToken,
    attempts,
    newAttemptId,
    isCurrent = () => true,
    onMarkedStale = () => {},
}) {
    const attemptIdFor = (fingerprint) => {
        if (!attempts.has(fingerprint)) attempts.set(fingerprint, newAttemptId());
        return attempts.get(fingerprint);
    };
    let remaining = picks;
    let review = null;
    let failure = '';
    let notice = '';
    const settle = (outcome, fingerprint, stagedPicks, staleMark) => {
        if (outcome.ok) {
            attempts.delete(fingerprint);
            remaining = stagedPicks(remaining);
            if (outcome.review) review = outcome.review;
            else notice = outcome.message;
            return true;
        }
        if (outcome.stale) {
            const marked = staleMark(remaining);
            onMarkedStale(remaining, marked);
            remaining = marked;
        } else {
            failure = outcome.message;
        }
        return false;
    };

    const items = toStageItems(remaining);
    let proceed = true;
    if (items.length) {
        const fingerprint = JSON.stringify(['items', items]);
        const response = await stageSecurityResolutionAdditions({
            attemptId: attemptIdFor(fingerprint),
            expectedWatchlistRevisionId,
            items,
        });
        if (!isCurrent()) return null;
        const outcome = additionStageOutcome(response, sessionScopeToken);
        proceed = settle(
            outcome,
            fingerprint,
            (current) => current.filter((pick) => !isListingPick(pick)),
            (current) => markStaleStageItems(current, staleStageItemIndices(outcome.detail, items.length)),
        );
    }
    // One at a time: each call amends the draft the previous one left.
    const requests = proceed ? toUnresolvedStageRequests(remaining) : [];
    for (const request of requests) {
        const fingerprint = JSON.stringify(['unresolved', request.previewToken, request.requestedCountry || '']);
        // eslint-disable-next-line no-await-in-loop
        const response = await stageSecurityResolutionUnresolvedAddition({
            attemptId: attemptIdFor(fingerprint),
            previewToken: request.previewToken,
            requestedCountry: request.requestedCountry,
            expectedWatchlistRevisionId,
        });
        if (!isCurrent()) return null;
        if (!settle(
            additionStageOutcome(response, sessionScopeToken),
            fingerprint,
            (current) => removePick(current, request.key),
            (current) => markStalePicks(current, [request.key]),
        )) break;
    }
    return { picks: remaining, review, failure, notice };
}
